import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EmptyState, ErrorState, LoadingState, PageHeader, StatusBadge } from "@/components/common/States";
import { ConfirmReasonDialog } from "@/components/common/ConfirmReasonDialog";
import { api } from "@/lib/api";
import { REVIEW_STATUS, ROLE_LABELS, formatDate } from "@/lib/labels";

const Side = ({
  title, name, nisn, nip,
  studentRegistrationNumber, employeeRegistrationNumber, extra,
}) => (
  <div className="rounded-lg bg-muted p-4 text-sm space-y-1 flex-1">
    <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{title}</p>
    <p className="font-semibold">{name}</p>
    {nisn && <p>NISN: <span className="font-mono">{nisn}</span></p>}
    {nip && <p>NIP: <span className="font-mono">{nip}</span></p>}
    {studentRegistrationNumber && (
      <p>NIS/NIPD/NISM/NISD: <span className="font-mono">{studentRegistrationNumber}</span></p>
    )}
    {employeeRegistrationNumber && (
      <p>NUPTK/NITK/NRG: <span className="font-mono">{employeeRegistrationNumber}</span></p>
    )}
    {extra}
  </div>
);

const ReviewCard = ({ r, onAct }) => (
  <article className="rounded-2xl border bg-card p-5 space-y-4" data-testid={`review-card-${r.id}`}>
    <div className="flex flex-wrap items-center justify-between gap-2">
      <p className="text-sm">Diajukan oleh <b>{r.school_name}</b> · {formatDate(r.created_at)}</p>
      <StatusBadge map={REVIEW_STATUS} value={r.status} />
    </div>
    <div className="flex flex-col md:flex-row gap-3">
      <Side
        title="Data yang diajukan"
        name={r.submitted.full_name}
        nisn={r.submitted.nisn}
        nip={r.submitted.nip}
        studentRegistrationNumber={r.submitted.student_registration_number}
        employeeRegistrationNumber={r.submitted.employee_registration_number}
        extra={<p>Peran: {ROLE_LABELS[r.submitted.role]}</p>}
      />
      <Side
        title="Identitas yang sudah ada"
        name={r.candidate.full_name}
        nisn={r.candidate.nisn}
        nip={r.candidate.nip}
        extra={<p>Terdaftar di {r.candidate.school_count} sekolah</p>}
      />
    </div>
    {r.status === "open" ? (
      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={() => onAct(r, "link")} data-testid={`review-link-${r.id}`}>Tautkan ke identitas yang ada</Button>
        <Button size="sm" variant="outline" onClick={() => onAct(r, "reject")} data-testid={`review-reject-${r.id}`}>Tolak pengajuan</Button>
      </div>
    ) : <p className="text-xs text-muted-foreground">Alasan: {r.resolution_reason}</p>}
  </article>
);

export default function IdentityReviews() {
  const [status, setStatus] = useState("open");
  const [act, setAct] = useState(null);
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["reviews", status], queryFn: () => api.get("/platform/identity-reviews", { params: { status } }).then((r) => r.data) });
  const confirm = async (reason) => {
    await api.post(`/platform/identity-reviews/${act.r.id}/resolve`, { decision: act.decision, reason });
    toast.success("Tinjauan diselesaikan.");
    qc.invalidateQueries();
  };
  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Registri Identitas Global" title="Tinjauan Identitas" description="NISN/NIP yang sudah terdaftar tidak digabung otomatis. Data akademik tetap terikat pada masing-masing sekolah." />
      <Tabs value={status} onValueChange={setStatus}>
        <TabsList>
          {Object.entries(REVIEW_STATUS).map(([k, v]) => <TabsTrigger key={k} value={k} data-testid={`reviews-tab-${k}`}>{v.label}</TabsTrigger>)}
        </TabsList>
      </Tabs>
      {q.isLoading ? <LoadingState /> : q.error ? <ErrorState error={q.error} onRetry={q.refetch} /> : !q.data.length ? (
        <EmptyState title="Tidak ada tinjauan" description="Belum ada pengajuan identitas pada kategori ini." />
      ) : q.data.map((r) => <ReviewCard key={r.id} r={r} onAct={(rv, decision) => setAct({ r: rv, decision })} />)}
      <ConfirmReasonDialog open={!!act} onOpenChange={(o) => !o && setAct(null)} danger={act?.decision === "reject"} testId="review-resolve-dialog"
        title={act?.decision === "link" ? "Tautkan identitas?" : "Tolak pengajuan?"}
        description={act?.decision === "link" ? "Sekolah pengaju akan mendapat keanggotaan yang merujuk identitas global yang sama. Data akademik tidak dibagikan." : "Pengajuan ditolak dan tidak ada keanggotaan yang dibuat."}
        confirmLabel={act?.decision === "link" ? "Tautkan" : "Tolak"} onConfirm={confirm} />
    </div>
  );
}
